// Minimal per-route SEO helper.
// Updates <title>, meta description, canonical, OG/Twitter tags,
// and can inject a JSON-LD structured-data block keyed by `jsonLdId`
// so we can cleanly replace it when the route changes.

import { useEffect } from "react";

const setMeta = (selector, attr, value) => {
  if (value == null) return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    // crude attribute parser: name="..." or property="..."
    const m = selector.match(/\[(\w+)="([^"]+)"\]/);
    if (m) el.setAttribute(m[1], m[2]);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
};

const setCanonical = (href) => {
  if (!href) return;
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const setJsonLd = (id, data) => {
  // Remove any previously-injected block with this id first (clean replace).
  const existing = document.head.querySelector(`script[data-ld="${id}"]`);
  if (existing) existing.remove();
  if (!data) return;
  const s = document.createElement("script");
  s.type = "application/ld+json";
  s.setAttribute("data-ld", id);
  s.textContent = JSON.stringify(data);
  document.head.appendChild(s);
};

// Canonical URLs always resolve against the production origin, never
// the current window's origin. Without this the preview/staging host
// (household-harmony-5.preview.emergentagent.com) ended up self-canonicalising
// when Google discovered it, triggering the noisy "Alternative page with
// proper canonical tag" notice in Search Console. Hard-pinning to
// famfeel.com means every environment funnels link-equity into prod.
const CANONICAL_ORIGIN = "https://famfeel.com";

export function useSeo({ title, description, path, image, jsonLd, jsonLdId } = {}) {
  useEffect(() => {
    if (title) document.title = title;
    if (description) {
      setMeta('meta[name="description"]', "content", description);
      setMeta('meta[property="og:description"]', "content", description);
      setMeta('meta[name="twitter:description"]', "content", description);
    }
    if (title) {
      setMeta('meta[property="og:title"]', "content", title);
      setMeta('meta[name="twitter:title"]', "content", title);
    }
    if (path) {
      const abs = `${CANONICAL_ORIGIN}${path}`;
      setCanonical(abs);
      setMeta('meta[property="og:url"]', "content", abs);
    }
    // Promote og:image to absolute URL so Facebook/LinkedIn honor it.
    const imgPath = image || "/og-image.png";
    const absImage = imgPath.startsWith("http") ? imgPath : `${CANONICAL_ORIGIN}${imgPath}`;
    setMeta('meta[property="og:image"]', "content", absImage);
    setMeta('meta[name="twitter:image"]', "content", absImage);
    if (jsonLdId) setJsonLd(jsonLdId, jsonLd);
    return () => {
      if (jsonLdId) setJsonLd(jsonLdId, null);
    };
  }, [title, description, path, image, jsonLdId, JSON.stringify(jsonLd || null)]);
}
